import { useState } from "react";
import { TeamCode, useTeamCodes } from "./TeamCodes";
import {
  kingStandings,
  mayScore,
  scoringIssue,
  placements,
  schedule,
  standings,
  type Actor,
  type Heat,
  type Match,
  type Standing,
  type Tournament,
} from "@spikeeasy/tournament";
import { formatTime, Notice, teamLabel, stageLabels, teamName } from "./ui";

export function MatchCard({
  t,
  match,
  actor,
  onScore,
  onManage,
}: {
  t: Tournament;
  match: Match;
  actor: Actor | null;
  onScore: (m: Match) => void;
  onManage: (m: Match) => void;
}) {
  const canScore =
    actor &&
    mayScore(t, match, actor) &&
    !scoringIssue(t, match, new Date().toISOString());
  const slotLabel = (side: 0 | 1) => {
    if (match.teams[side]) return teamName(t, match.teams[side]);
    const slot = match.slots[side];
    return "matchId" in slot
      ? `${slot.outcome === "winner" ? "Winner" : "Loser"} of ${slot.matchId}`
      : "Bye";
  };
  return (
    <article className={`match-card ${match.status}`}>
      <div className="match-meta">
        <span>
          {match.id} ·{" "}
          {match.poolId ? `Pool ${match.poolId}` : stageLabels[match.stage]}
        </span>
        <span>
          {match.status === "live"
            ? "Playing now"
            : match.resolution === "bye"
              ? "Bye"
              : match.status === "complete"
                ? "Final score"
                : match.needsRelease && !match.released
                  ? "Organizer release"
                  : "Up next"}
        </span>
      </div>
      <div className="match-teams">
        {([0, 1] as const).map((side) => (
          <div
            key={side}
            className={match.winnerId === match.teams[side] ? "winner" : ""}
          >
            <span>{slotLabel(side)}</span>
            {match.status === "live" ? (
              <strong>{match.points[side]}</strong>
            ) : (
              match.sets.length > 0 && (
                <span className="set-results">
                  {match.sets.map((set, i) => (
                    <strong key={i}>{set[side]}</strong>
                  ))}
                </span>
              )
            )}
          </div>
        ))}
      </div>
      <p className="match-note">
        {match.resolution === "forfeit"
          ? "Walkover"
          : match.resolution === "retirement"
            ? "Retirement"
            : match.refereeMode === "self"
              ? "Self-ref"
              : match.refereeId
                ? `Ref: ${teamName(t, match.refereeId)}`
                : match.refereeSlot && "matchId" in match.refereeSlot
                  ? `Ref: ${match.refereeSlot.outcome === "loser" ? "Loser" : "Winner"} of ${match.refereeSlot.matchId}`
                  : "Organizer: choose a referee or self-ref"}
      </p>
      {match.notBefore && (
        <p className="match-note">
          Not before {formatTime(match.notBefore, t.timezone)}
        </p>
      )}
      {(canScore || actor?.role === "admin") && (
        <div className="actions">
          {canScore && (
            <button className="primary" onClick={() => onScore(match)}>
              Score match
            </button>
          )}
          {actor?.role === "admin" && match.resolution !== "bye" && (
            <button className="text-button" onClick={() => onManage(match)}>
              {match.status === "complete" ? "Review result" : "Manage match"}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

export function NowView({
  t,
  actor,
  onScore,
  onManage,
  onHeat,
  startHeat,
  now,
}: {
  t: Tournament;
  actor: Actor | null;
  onScore: (m: Match) => void;
  onManage: (m: Match) => void;
  onHeat: (h: Heat) => void;
  startHeat: (h: Heat) => void;
  now: string;
}) {
  const [selectedTeam, setSelectedTeam] = useState("");
  const estimates = schedule(t, now);
  const matches = t.matches.filter(
    (m) =>
      m.resolution !== "bye" &&
      (!selectedTeam ||
        m.teams.includes(selectedTeam) ||
        m.refereeId === selectedTeam),
  );
  return (
    <div className="stack">
      <div className="section-heading">
        <h2>{t.status === "finished" ? "That’s a wrap." : "On the courts"}</h2>
        <label className="filter">
          <span className="sr-only">Filter by team</span>
          <select
            value={selectedTeam}
            onChange={(e) => setSelectedTeam(e.target.value)}
          >
            <option value="">All teams</option>
            {t.teams.map((team) => (
              <option key={team.id} value={team.id}>
                {teamLabel(team)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {t.status === "draft" && (
        <Notice>
          This is a draft. Only organizers can see it. Review the draw before
          publishing.
        </Notice>
      )}
      {t.status === "finished" ? (
        <Results t={t} />
      ) : (
        <div className="court-grid">
          {t.courts.map((court) => {
            const live = matches.find(
              (m) => m.courtId === court.id && m.status === "live",
            );
            const next = matches.find(
              (m) =>
                m.status === "ready" && (!m.courtId || m.courtId === court.id),
            );
            const heat = t.heats.find(
              (h) =>
                h.courtId === court.id &&
                h.status !== "complete" &&
                (!selectedTeam || h.teamIds.includes(selectedTeam)),
            );
            return (
              <section className="court" key={court.id}>
                <div className="section-heading">
                  <h3>{court.name}</h3>
                  {court.netTeamId && (
                    <small>Net: {teamName(t, court.netTeamId)}</small>
                  )}
                </div>
                {live ? (
                  <MatchCard
                    t={t}
                    match={live}
                    actor={actor}
                    onScore={onScore}
                    onManage={onManage}
                  />
                ) : heat ? (
                  <HeatCard
                    t={t}
                    heat={heat}
                    actor={actor}
                    onHeat={onHeat}
                    startHeat={startHeat}
                    now={now}
                  />
                ) : (
                  <p className="empty">
                    {selectedTeam
                      ? "No current match for this team here."
                      : "Court is between games."}
                  </p>
                )}
                {next && (
                  <div className="next-up">
                    <small>
                      Coming up ·{" "}
                      {formatTime(
                        estimates.find((s) => s.id === next.id)?.estimatedAt,
                        t.timezone,
                      )}
                    </small>
                    <MatchCard
                      t={t}
                      match={next}
                      actor={actor}
                      onScore={onScore}
                      onManage={onManage}
                    />
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
      <p className="muted">
        Reference times can move; “not before” times are firm.
      </p>
      {matches.some(
        (m) => m.status === "complete" && m.resolution !== "bye",
      ) && (
        <section className="stack">
          <h2>Recent results</h2>
          <div className="match-grid">
            {matches
              .filter((m) => m.status === "complete" && m.resolution !== "bye")
              .slice(-6)
              .reverse()
              .map((match) => (
                <MatchCard
                  key={match.id}
                  t={t}
                  match={match}
                  actor={actor}
                  onScore={onScore}
                  onManage={onManage}
                />
              ))}
          </div>
        </section>
      )}
    </div>
  );
}

function HeatCard({
  t,
  heat,
  actor,
  onHeat,
  startHeat,
  now,
}: {
  t: Tournament;
  heat: Heat;
  actor: Actor | null;
  onHeat: (h: Heat) => void;
  startHeat: (h: Heat) => void;
  now: string;
}) {
  const maySubmit =
    actor &&
    (actor.role === "admin" ||
      (actor.tournamentId === t.id && heat.teamIds.includes(actor.teamId)));
  const seconds = heat.startedAt
    ? Math.max(
        0,
        Math.ceil(
          (Date.parse(heat.startedAt) +
            t.config.heatMinutes * 60000 -
            Date.parse(now)) /
            1000,
        ),
      )
    : t.config.heatMinutes * 60;
  return (
    <article className="heat-card stack">
      <div className="section-heading">
        <strong>Heat {heat.round}</strong>
        <span>
          {heat.status === "ready"
            ? `${t.config.heatMinutes} minutes`
            : `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")} left`}
        </span>
      </div>
      <ul className="plain-list">
        {heat.teamIds.map((id) => (
          <li key={id}>{teamName(t, id)}</li>
        ))}
      </ul>
      {maySubmit && (
        <button
          className="primary"
          onClick={() =>
            heat.status === "ready" ? startHeat(heat) : onHeat(heat)
          }
        >
          {heat.status === "ready" ? "Start heat" : "Enter final totals"}
        </button>
      )}
    </article>
  );
}

export function StandingsView({ t }: { t: Tournament }) {
  if (t.status === "finished") return <Results t={t} />;
  if (["single", "double"].includes(t.config.mode))
    return (
      <Notice>
        Final placements appear when the tournament finishes. Follow each team
        in the draw meanwhile.
      </Notice>
    );
  const groups =
    t.config.mode === "pools"
      ? t.pools.map((pool) => ({
          name: `Pool ${pool.id}`,
          rows: standings(t, pool.id),
        }))
      : [
          {
            name: t.config.mode === "king" ? "Heat totals" : "Swiss standings",
            rows: t.config.mode === "king" ? kingStandings(t) : standings(t),
          },
        ];
  return (
    <div className="stack">
      <h2>Standings</h2>
      {groups.map((group) => (
        <section className="stack" key={group.name}>
          <h3>{group.name}</h3>
          <StandingsTable t={t} rows={group.rows} label={group.name} />
          {group.rows.some((row) => row.tied) && (
            <p className="muted">
              Ties are provisional until play finishes. Any tie the results
              cannot settle goes to the organizer.
            </p>
          )}
        </section>
      ))}
      <details>
        <summary>How standings work</summary>
        <p>
          {t.config.mode === "king"
            ? "Only king-side rally wins score points. A missed serve gives no king-side point. Sum the points from every heat; the organizer resolves any remaining tie."
            : t.config.mode === "swiss"
              ? "Match wins, then opponents’ combined wins, head-to-head, sets won, then points won. A bye gives one match win and no invented set or point scores."
              : "Match wins, then head-to-head, sets won, then points won. For a multi-team tie, compare wins between the tied teams if they have all met. Qualification takes the same base number from each pool and fills remaining places from the next-placed teams. Cross-pool comparisons use sets won, then points won, per played match when counts differ."}
        </p>
      </details>
    </div>
  );
}
export function StandingsTable({
  t,
  rows,
  label,
}: {
  t: Tournament;
  rows: Standing[];
  label: string;
}) {
  return (
    <table className="standings-table" role="table" aria-label={label}>
      <thead>
        <tr>
          <th scope="col">Team</th>
          {t.config.mode !== "king" && (
            <th scope="col">
              <abbr title="Match wins">W</abbr>
            </th>
          )}
          {t.config.mode === "swiss" && (
            <th scope="col">
              <abbr title="Opponents’ match wins">OW</abbr>
            </th>
          )}
          {t.config.mode !== "king" && (
            <th scope="col">
              <abbr title="Sets won : sets lost">Sets</abbr>
            </th>
          )}
          <th scope="col">
            {t.config.mode === "king" ? (
              "Points"
            ) : (
              <abbr title="Points won : points lost">Points</abbr>
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.teamId}>
            <th scope="row">
              <span className="standing-place">{row.place}.</span>{" "}
              {teamName(t, row.teamId)}
              {t.teams.find((team) => team.id === row.teamId)?.withdrawn && (
                <small> · Withdrawn</small>
              )}
            </th>
            {t.config.mode !== "king" && <td data-label="Wins">{row.wins}</td>}
            {t.config.mode === "swiss" && (
              <td data-label="Opp. wins">{row.opponentsWins}</td>
            )}
            {t.config.mode !== "king" && (
              <td data-label="Sets W : L">
                {row.sets} : {row.setsLost}
              </td>
            )}
            <td
              data-label={t.config.mode === "king" ? "Points" : "Points W : L"}
            >
              {t.config.mode === "king"
                ? row.points
                : `${row.points} : ${row.pointsLost}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
export function TeamsView({
  t,
  actor,
}: {
  t: Tournament;
  actor: Actor | null;
}) {
  const admin = actor?.role === "admin";
  const { codes, error, busy, generate, retry } = useTeamCodes(t.id, admin);
  return (
    <div className="stack">
      <div className="section-heading">
        <h2>The teams</h2>
      </div>
      {admin && (
        <p className="muted">
          Share each access code with its team. Only organizers can see this
          list.
        </p>
      )}
      {admin && !codes && !error && <Notice>Loading team codes…</Notice>}
      {admin && error && (
        <Notice error>
          {error}{" "}
          <button type="button" className="text-button" onClick={retry}>
            Retry
          </button>
        </Notice>
      )}
      <ol className="team-list">
        {t.teams
          .toSorted((a, b) => a.seed - b.seed)
          .map((team) => (
            <li key={team.id}>
              <div>
                <strong>{teamLabel(team)}</strong>
                <p>{team.players.map((p) => p.name).join(" + ")}</p>
                <small className="muted">
                  {team.withdrawn
                    ? "Withdrawn"
                    : team.bringsNet
                      ? "Bringing a net"
                      : ""}
                  {actor?.role === "admin" &&
                    ` · ${team.players.reduce((sum, p) => sum + p.points, 0)} points at seeding`}
                </small>
              </div>
              {admin && codes && (
                <TeamCode
                  team={team}
                  code={codes.find((row) => row.teamId === team.id)?.code}
                  busy={busy}
                  replace={() => void generate([team.id])}
                />
              )}
            </li>
          ))}
      </ol>
    </div>
  );
}
function Results({ t }: { t: Tournament }) {
  return (
    <section className="stack">
      <h2>Final results</h2>
      <table>
        <thead>
          <tr>
            <th>Place</th>
            <th>Team</th>
            <th>Points per player</th>
          </tr>
        </thead>
        <tbody>
          {placements(t).map((row) => (
            <tr key={row.teamId}>
              <td>{row.place}</td>
              <th>{teamName(t, row.teamId)}</th>
              <td>{row.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted">
        Awards use {t.config.pointsTable.name}. Each player receives the full
        award, valid for one year.
      </p>
    </section>
  );
}
